import Vue from 'vue';
import VueRouter from 'vue-router';
import { authGuard, superAdminGuard } from '@/auth';
import ReviewsPage from '@/pages/ReviewsPage';
import ReviewSettingsPage from '@/pages/ReviewSettingsPage';
import DataExtractionPage from '@/pages/DataExtractionPage';
import DataExtractionConsensusPage from '@/pages/DataExtractionConsensusPage';
import UploadsPage from '@/pages/UploadsPage';
import ReviewPageV2 from '@/pages/ReviewPageV2';
import ScreeningPageV2 from '@/pages/ScreeningPageV2';
import FullTextReviewPageV2 from '@/pages/FullTextReviewPageV2';
import AdminPage from '@/pages/AdminPage';
import PasswordResetPage from '@/pages/PasswordResetPage';

Vue.use(VueRouter);

// vue-router 3 only supports a single beforeEnter function (guard arrays are a
// vue-router 4 feature), so chain the guards: authenticate first, then check
// super admin only if authGuard allowed the navigation through.
const adminGuard = (to, from, next) =>
  authGuard(to, from, (route) =>
    route === undefined
      ? superAdminGuard(to, from, next)
      : next(route)
  );

const routes = [
  {
    path: '/password-reset',
    name: 'PasswordResetPage',
    component: PasswordResetPage,
    // No auth guard - this page is public
  },
  {
    path: '/admin',
    name: 'AdminPage',
    component: AdminPage,
    beforeEnter: adminGuard,
  },
  {
    path: '/admin/:tab',
    name: 'AdminPageTab',
    component: AdminPage,
    beforeEnter: adminGuard,
  },
  {
    path: '/reviews/:tab',
    name: 'ReviewPageTab',
    component: ReviewsPage,
    beforeEnter: authGuard,
  },
  {
    path: '/reviews',
    name: 'ReviewsPage',
    component: ReviewsPage,
    beforeEnter: authGuard,
  },
  // Redirect old V1 paths to V2 equivalents. Screening conflicts are resolved
  // in the V2 Screen tab, and full-text conflicts in the V2 Review tab, so the
  // V1 conflict pages map there.
  {
    path: '/review/v1/:reviewId/screening',
    redirect: (to) =>
      `/review/${to.params.reviewId}/screening/screen`,
  },
  {
    path: '/review/v1/:reviewId/conflicts',
    redirect: (to) =>
      `/review/${to.params.reviewId}/screening/screen`,
  },
  {
    path: '/review/v1/:reviewId/duplicates/:tab?',
    redirect: (to) => `/review/${to.params.reviewId}/duplicates`,
  },
  {
    path: '/review/v1/:reviewId/full-text-review/conflicts',
    redirect: (to) =>
      `/review/${to.params.reviewId}/full-text-review`,
  },
  {
    path: '/review/v1/:reviewId/full-text-review',
    redirect: (to) =>
      `/review/${to.params.reviewId}/full-text-review`,
  },
  {
    path: '/review/v1/:reviewId/*',
    redirect: (to) => `/review/${to.params.reviewId}`,
  },
  {
    path: '/review/v1/:reviewId',
    redirect: (to) => `/review/${to.params.reviewId}`,
  },
  {
    path: '/review/:reviewId/screening/:tab',
    name: 'ScreeningPageV2Tab',
    component: ScreeningPageV2,
    beforeEnter: authGuard,
  },
  {
    path: '/review/:reviewId/screening',
    name: 'ScreeningPageV2',
    component: ScreeningPageV2,
    beforeEnter: authGuard,
  },
  {
    path: '/review/:reviewId/settings/:tab',
    name: 'ReviewSettingsPageTab',
    component: ReviewSettingsPage,
    beforeEnter: authGuard,
  },
  {
    path: '/review/:reviewId/settings',
    name: 'ReviewSettingsPage',
    component: ReviewSettingsPage,
    beforeEnter: authGuard,
  },
  {
    path: '/review/:reviewId/full-text-review/:tab',
    name: 'FullTextReviewPageV2Tab',
    component: FullTextReviewPageV2,
    beforeEnter: authGuard,
  },
  {
    path: '/review/:reviewId/full-text-review',
    name: 'FullTextReviewPageV2',
    component: FullTextReviewPageV2,
    beforeEnter: authGuard,
  },
  {
    path: '/review/:reviewId/data-extraction',
    name: 'DataExtractionPage',
    component: DataExtractionPage,
    beforeEnter: authGuard,
  },
  {
    path: '/review/:reviewId/consensus-review',
    name: 'DataExtractionConsensusPage',
    component: DataExtractionConsensusPage,
    beforeEnter: authGuard,
  },
  {
    path: '/review/:reviewId/uploads',
    name: 'UploadsPage',
    component: UploadsPage,
    beforeEnter: authGuard,
  },
  {
    path: '/review/:reviewId/:tab',
    name: 'ReviewPageV2Tab',
    component: ReviewPageV2,
    beforeEnter: authGuard,
  },
  {
    path: '/review/:reviewId',
    name: 'ReviewPageV2',
    component: ReviewPageV2,
    beforeEnter: authGuard,
  },
  {
    path: '/*',
    redirect: '/reviews',
  },
];

const router = new VueRouter({
  mode: 'history',
  routes,
});

export default router;
